import { useMutation } from '@tanstack/react-query';
import { remixApi } from '@tectonic/api-client';
import { script } from '@tectonic/halo-script';
import {
  ElemasonWidgetActionType,
  NavigationActionType,
  type ElemasonSocialRewardWidget,
} from '@tectonic/types';
import { access } from '@tectonic/utils';
import { type FC, type MouseEvent } from 'react';
import { Button } from '../../components';
import { useActionDispatch } from '../../hooks';
import { useHaloScript } from '../../hooks/useHaloScript';

import type { AxiosError } from 'axios';

interface ElemasonSocialRewardWidgetProps {
  widget: ElemasonSocialRewardWidget;
}

const IS_ANONYMOUS_ACCESSOR = script([
  access(['page', 'currentUser', 'isAnonymous']),
]);

const SocialRewardWidget: FC<ElemasonSocialRewardWidgetProps> = ({
  widget: { data, config, actions },
}) => {
  const dispatch = useActionDispatch();

  const event = useHaloScript(data?.event);

  const isAnonymous = useHaloScript<boolean>(IS_ANONYMOUS_ACCESSOR);

  const mutation = useMutation({
    mutationFn: () => remixApi.triggerSocialEvent({ event: event! }),
    onSuccess: (resp) => {
      actions?.onSuccess?.forEach((action) => dispatch(action, resp));
    },
    onError: (error: AxiosError) => {
      actions?.onError?.forEach((action) => dispatch(action, error));
    },
  });

  const onClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (isAnonymous) {
      dispatch({
        type: ElemasonWidgetActionType.NAVIGATE_TO,
        payload: {
          type: NavigationActionType.PATH,
          slug: 'login',
        },
      });
    } else {
      mutation.mutate();
    }
  };

  if (!event) return null;

  return (
    <Button
      data={data}
      onClick={onClick}
      config={config?.button}
      size={config?.button?.size}
      isLoading={mutation.isPending}
      variant={config?.button?.variant}
      modifier={config?.button?.modifier}
      style={{
        color: config?.button?.text?.color,
        borderColor: config?.button?.borderColor,
        borderRadius: config?.button?.borderRadius,
        backgroundColor: config?.button?.backgroundColor,
      }}
    />
  );
};

export { SocialRewardWidget };
